// /history — saved reflections for this device (Phase 3).
// Anonymous, device-scoped. Delete + export controls per PRD.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Phone,
  Trash2,
} from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PhoneLinkCard from "@/components/PhoneLinkCard";
import { getDeviceId } from "@/lib/device";
import { track } from "@/lib/analytics";
import {
  isSignedIn,
  getAuthPhone,
  setAuth,
  clearAuth,
  maskPhone,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return ""; // unparseable date — render nothing rather than crash
  }
}

function download(filename, text, type = "text/plain") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function reflectionToText(r) {
  const lines = [
    r.title,
    formatDate(r.created_at),
    "",
    r.summary,
  ];
  if (r.transcript && r.transcript.length) {
    lines.push("", "— Transcript —");
    r.transcript.forEach((m) => lines.push(`${m.who}: ${m.text}`));
  }
  lines.push("", "Saved with Call God");
  return lines.join("\n");
}

export default function History() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reflections, setReflections] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [signedIn, setSignedIn] = useState(() => isSignedIn());
  const [authPhone, setAuthPhone] = useState(() => getAuthPhone());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let res;
        if (signedIn) {
          // Signed in: merged history. The httpOnly session cookie rides
          // along automatically on this same-origin request.
          res = await fetch(`${API}/me/reflections`);
          if (res.status === 401) {
            // Session expired — fall back to anonymous device history.
            clearAuth();
            if (!cancelled) {
              setSignedIn(false);
              setAuthPhone("");
            }
            res = null;
          }
        }
        if (!res) {
          res = await fetch(
            `${API}/reflections?device_id=${encodeURIComponent(getDeviceId())}`,
          );
        }
        if (!res.ok) throw new Error(`http_${res.status}`);
        const data = await res.json();
        if (!cancelled) setReflections(data.reflections || []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, signedIn]);

  useEffect(() => {
    track("history_viewed");
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    setReloadKey((k) => k + 1);
  }, []);

  const onLinked = useCallback((phone) => {
    setAuth(phone);
    setSignedIn(true);
    setAuthPhone(phone);
    setLoading(true);
    setError(false);
    setReloadKey((k) => k + 1);
  }, []);

  const signOut = useCallback(async () => {
    clearAuth();
    setSignedIn(false);
    setAuthPhone("");
    setLoading(true);
    try {
      // Server deletes the token + clears the httpOnly cookie.
      await fetch(`${API}/auth/logout`, { method: "POST" });
    } catch {
      /* local state already cleared */
    }
  }, []);

  const remove = async (id) => {
    setDeletingId(id);
    try {
      const res = await fetch(
        `${API}/reflections/${id}?device_id=${encodeURIComponent(getDeviceId())}`,
        { method: "DELETE" }, // cookie authorizes phone-call reflections
      );
      if (res.ok || res.status === 404) {
        setReflections((prev) => prev.filter((r) => r.id !== id));
      }
    } catch {
      /* leave the item; user can retry */
    } finally {
      setDeletingId(null);
      setConfirmingId(null);
    }
  };

  const exportAll = () => {
    download(
      "callgod-reflections.json",
      JSON.stringify({ exported_at: new Date().toISOString(), reflections }, null, 2),
      "application/json",
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Nav />

      <main
        data-testid="history-page"
        className="mx-auto w-full max-w-3xl flex-1 px-5 pb-10 pt-10 lg:px-8"
      >
        <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Your history
        </p>
        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
          <h1
            className="font-serif-display text-foreground"
            style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)", lineHeight: 1.2 }}
          >
            Saved reflections
          </h1>
          {reflections.length > 0 ? (
            <button
              type="button"
              data-testid="export-all-btn"
              onClick={exportAll}
              className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]"
            >
              <span className="inline-flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
                Export all
              </span>
            </button>
          ) : null}
        </div>
        <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
          {signedIn
            ? "Your browser saves and phone calls, gathered in one place."
            : "Kept privately on this device, until you let them go."}
        </p>

        {signedIn ? (
          <div
            data-testid="signed-in-banner"
            className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3"
            style={{ background: "hsla(43, 50%, 90%, 0.7)" }}
          >
            <Phone className="h-4 w-4" style={{ color: "hsl(42 50% 40%)" }} strokeWidth={1.8} />
            <p className="mr-auto text-[13px] text-foreground/85">
              Linked to {maskPhone(authPhone)} — phone call history included.
            </p>
            <button
              type="button"
              data-testid="auth-sign-out"
              onClick={signOut}
              className="text-[13px] text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              Sign out
            </button>
            <button
              type="button"
              data-testid="manage-subscription-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/billing/portal`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      origin_url: window.location.origin,
                      device_id: getDeviceId(),
                    }),
                  });
                  const data = await res.json().catch(() => ({}));
                  if (res.ok && data.url) window.location.href = data.url;
                } catch {
                  /* no active subscription or portal unavailable */
                }
              }}
              className="text-[13px] text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              Manage subscription
            </button>
          </div>
        ) : (
          <PhoneLinkCard onLinked={onLinked} />
        )}

        {loading ? (
          <div className="mt-14 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="cg-card mt-10 rounded-2xl p-6 text-center">
            <p className="text-sm text-foreground/85">
              Could not load your reflections right now.
            </p>
            <button
              type="button"
              onClick={load}
              className="cg-btn-primary mt-4 !min-h-[42px] !px-5 text-sm"
            >
              Retry
            </button>
          </div>
        ) : null}

        {!loading && !error && reflections.length === 0 ? (
          <div
            data-testid="history-empty"
            className="cg-card mt-10 rounded-2xl p-8 text-center"
          >
            <BookOpen
              className="mx-auto h-6 w-6"
              style={{ color: "hsl(42 50% 50%)" }}
              strokeWidth={1.6}
            />
            <p className="font-serif-display mt-3 text-xl text-foreground">
              Nothing saved yet.
            </p>
            <p className="mx-auto mt-1.5 max-w-xs text-sm leading-6 text-muted-foreground">
              At the end of a call you can keep a short reflection of what was
              spoken. They will gather here.
            </p>
            <Link
              to="/call"
              data-testid="history-start-call"
              className="cg-btn-primary mt-5 inline-flex !min-h-[44px] !px-6 text-sm"
            >
              <span className="inline-flex items-center gap-2">
                <Phone className="h-4 w-4" strokeWidth={1.8} />
                Start a Call
              </span>
            </Link>
          </div>
        ) : null}

        <div className="mt-8 flex flex-col gap-4">
          {reflections.map((r) => (
            <article
              key={r.id}
              data-testid="reflection-card"
              className="cg-card rounded-2xl p-5 sm:p-6"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif-display text-xl leading-7 text-foreground">
                    {r.title}
                  </h2>
                  <p className="mt-0.5 flex items-center gap-2 text-[12px] text-muted-foreground">
                    {formatDate(r.created_at)}
                    {r.source === "phone" ? (
                      <span
                        data-testid="phone-badge"
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em]"
                        style={{
                          background: "hsla(214, 40%, 88%, 0.8)",
                          color: "hsl(214 35% 35%)",
                        }}
                      >
                        <Phone className="h-2.5 w-2.5" strokeWidth={2} />
                        Phone call
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label="Export reflection"
                    data-testid="reflection-export-btn"
                    onClick={() =>
                      download(
                        `callgod-reflection-${r.id.slice(0, 8)}.txt`,
                        reflectionToText(r),
                      )
                    }
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Download className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete reflection"
                    data-testid="reflection-delete-btn"
                    onClick={() => setConfirmingId(r.id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-[hsl(0,60%,42%)]"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              </div>

              <p className="mt-3 text-[15px] leading-7 text-foreground/90">
                {r.summary}
              </p>

              {r.has_transcript && r.transcript ? (
                <div className="mt-3">
                  <button
                    type="button"
                    data-testid="reflection-transcript-toggle"
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))
                    }
                    className="inline-flex items-center gap-1 text-[13px] text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                  >
                    {expanded[r.id] ? (
                      <>
                        Hide transcript <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Show transcript <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                  {expanded[r.id] ? (
                    <div
                      className="mt-3 flex flex-col gap-2.5 rounded-xl p-4"
                      style={{ background: "hsla(43, 50%, 92%, 0.6)" }}
                    >
                      {r.transcript.map((m, i) => (
                        <p key={`${r.id}-${i}-${m.who}`} className="text-[14px] leading-6">
                          <span
                            className="mr-1.5 text-[11px] uppercase tracking-[0.14em]"
                            style={{
                              color:
                                m.who === "God"
                                  ? "hsl(42 50% 40%)"
                                  : "hsl(30 10% 45%)",
                            }}
                          >
                            {m.who}
                          </span>
                          <span className="text-foreground/90">{m.text}</span>
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {confirmingId === r.id ? (
                <div
                  data-testid="delete-confirm"
                  className="mt-4 flex flex-wrap items-center gap-2 rounded-xl px-4 py-3"
                  style={{ background: "hsla(0, 50%, 92%, 0.7)" }}
                >
                  <p className="mr-auto text-[13px] text-foreground/85">
                    Let this reflection go?
                  </p>
                  <button
                    type="button"
                    data-testid="delete-confirm-yes"
                    onClick={() => remove(r.id)}
                    disabled={deletingId === r.id}
                    className="inline-flex min-h-[36px] items-center rounded-full px-4 text-[13px] text-white"
                    style={{ background: "hsl(0 55% 42%)" }}
                  >
                    {deletingId === r.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      "Delete"
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid="delete-confirm-no"
                    onClick={() => setConfirmingId(null)}
                    className="inline-flex min-h-[36px] items-center rounded-full border px-4 text-[13px] text-foreground/80"
                    style={{ borderColor: "hsla(38, 25%, 70%, 0.7)" }}
                  >
                    Keep
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
